import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useCallback, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Divider,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDate, fDateTime } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';
import { hasPermission } from 'src/utils/has-permissions';
import {
  TABLE_HEAD_CELL_TYPE,
  TRANSACTION_PAYMENT_STATUS,
  TRANSACTION_STATUS,
} from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { useAppDispatcher } from 'src/redux/store';
import { AuthPermissions } from 'src/auth/permissions';
import { kitchenPurchaseOrderMarkAsCompleted } from 'src/redux/slices/kitchen-purchase-order';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { IKitchenPurchaseOrderDetailedItem } from 'src/types/kitchen-purchase';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemNo', label: '#', align: 'left', primary: true },
  {
    id: 'itemName',
    label: 'Item Name',
    align: 'left',
    primary: true,
    minWidth: 300,
  },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
  { id: 'expiryDate', label: 'Expiry Date', align: 'left', primary: true },
  { id: 'batchNumber', label: 'Batch #', align: 'left', primary: true },
  {
    id: 'unitPrice',
    label: 'Unit Price',
    align: 'right',
    primary: true,
  },
  { id: 'discount', label: 'Discount', align: 'right', primary: true },
  { id: 'total', label: 'Total', align: 'right', primary: true },
];

type Props = {
  currentKitchenPurchaseOrder: IKitchenPurchaseOrderDetailedItem;
};

export default function KitchenPurchaseOrderDetails({ currentKitchenPurchaseOrder }: Props) {
  const dispatch = useAppDispatcher();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user: authUser } = useAuthContext();

  const [isLoading, setIsLoading] = useState(false);

  const markAsCompleted = useCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(
        kitchenPurchaseOrderMarkAsCompleted({
          purchaseOrderId: currentKitchenPurchaseOrder?._id,
          purchaseOrder: currentKitchenPurchaseOrder,
        })
      ).unwrap();
      setIsLoading(false);

      enqueueSnackbar('Purchase order marked as completed');
      navigate(paths.dashboard.kitchen.purchase.root());
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
      setIsLoading(false);
    }
  }, [dispatch, currentKitchenPurchaseOrder, enqueueSnackbar, navigate]);

  const hasRequiredPermissions =
    (currentKitchenPurchaseOrder.status === TRANSACTION_STATUS.DRAFT &&
      hasPermission(
        authUser,
        'any',
        AuthPermissions.CREATE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.UPDATE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.DELETE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )) ||
    (currentKitchenPurchaseOrder.status !== TRANSACTION_STATUS.DRAFT &&
      hasPermission(
        authUser,
        'any',
        AuthPermissions.UPDATE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.DELETE_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      ));

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          {/* <Tabs
            value={tabValue}
            onChange={(_, value) => setTabValue(value)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs> */}

          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Basic Information</Typography>

              {hasRequiredPermissions && (
                <Link
                  component={RouterLink}
                  href={paths.dashboard.kitchen.purchase.edit(currentKitchenPurchaseOrder._id)}
                >
                  <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                    Edit
                  </Button>
                </Link>
              )}
            </Grid>

            {currentKitchenPurchaseOrder?.status === TRANSACTION_STATUS.COMPLETED && (
              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentKitchenPurchaseOrder?.transactionCode}
                  secondary="Transaction No"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            )}

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fDateTime(currentKitchenPurchaseOrder?.purchaseOrDispatchDate)}
                secondary="Purchase Date"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={
                  <Label
                    variant="soft"
                    color={
                      (currentKitchenPurchaseOrder?.paymentStatus ===
                        TRANSACTION_PAYMENT_STATUS.PAID &&
                        'success') ||
                      (currentKitchenPurchaseOrder?.paymentStatus ===
                        TRANSACTION_PAYMENT_STATUS.NOT_PAID &&
                        'error') ||
                      (currentKitchenPurchaseOrder?.paymentStatus ===
                        TRANSACTION_PAYMENT_STATUS.PARTIALLY_PAID &&
                        'warning') ||
                      'default'
                    }
                  >
                    {currentKitchenPurchaseOrder?.paymentStatus?.split('_').join(' ')}
                  </Label>
                }
                secondary="Payment Status"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={
                  <Label
                    variant="soft"
                    color={
                      (currentKitchenPurchaseOrder?.status === TRANSACTION_STATUS.COMPLETED &&
                        'success') ||
                      (currentKitchenPurchaseOrder?.status === TRANSACTION_STATUS.DRAFT &&
                        'default') ||
                      'default'
                    }
                  >
                    {currentKitchenPurchaseOrder?.status?.split('_').join(' ')}
                  </Label>
                }
                secondary="Order Status"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fCurrencyRupees(Number(currentKitchenPurchaseOrder?.discount ?? 0) * 100)}
                secondary="Discount"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fCurrencyRupees(
                  Number(currentKitchenPurchaseOrder?.totalAfterDiscount ?? 0) * 100
                )}
                secondary="Grand Total"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            </Grid>

            {currentKitchenPurchaseOrder?.supplier && (
              <Grid xs={12} md={4}>
                <ListItemText
                  primary={
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.suppliers.view(
                        currentKitchenPurchaseOrder.supplier._id
                      )}
                    >
                      {currentKitchenPurchaseOrder.supplier.supplierName}
                    </Link>
                  }
                  secondary="Supplier"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            )}

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                <Scrollbar>
                  <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
                    <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                    <TableBody>
                      {currentKitchenPurchaseOrder?.items.map((item, index) => (
                        <TableRow hover key={item._id}>
                          <TableCell align="left">
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.kitchen.view(item.inventoryItem._id)}
                            >
                              {item.inventoryItem.itemCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.inventoryItem.itemName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.quantity} {item.unit.abbreviation}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.expiryDate ? (
                              fDate(item.expiryDate)
                            ) : (
                              <Label variant="soft" color="default">
                                Unknown Expiry
                              </Label>
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.batchNumber || '-'}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {fCurrencyRupees(item.unitPrice * 100)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {fCurrencyRupees(item.discount * 100)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {fCurrencyRupees(item.totalAfterDiscount * 100)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Link component={RouterLink} href={paths.dashboard.kitchen.purchase.root()}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>

            {currentKitchenPurchaseOrder.status === TRANSACTION_STATUS.DRAFT && (
              <LoadingButton
                type="button"
                variant="contained"
                loading={isLoading}
                onClick={markAsCompleted}
              >
                Mark as completed
              </LoadingButton>
            )}
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
